import EmojiConvertor from 'emoji-js';
import {compact, toArray} from 'lodash';
import memoize from 'lru-memoize';

import {
  buildEmojiConvertor,
  EmojiDefinition,
  EmojiMap,
  isCustomEmoji,
} from '../../../src/services/emojis/emojis';
import {SkinTonesEnum} from '../../../src/services/emojis/emojiTypes';
import {markdownEmojiSplitRegexp, unifiedEmojiSplitRegexp} from './emojiRegexp';

export const emojiRegexp = /:([\w+-]+):(?::skin-tone-([2-6]):)?/i;
const emojiValidateRegexp = new RegExp(`^${emojiRegexp.source}$`, 'i');
const emojiUrlRegexp = /src="([\w\W]+.png)"/;

/*
 * Validation.
 */

/** Check whether a combination of shortCode and skinVariation is a valid Emoji. */
export function isValidShortcode(source: string, validationMap: EmojiMap) {
  // Check if this matches the format of an emoji.
  const match = source.match(emojiValidateRegexp);
  if (!match) {
    return false;
  }

  // Extract the shortcode and potential skin variation.
  const [, shortcode, variationId] = match;

  // Check if we have an emoji for this shortcode.
  const emoji = shortcode && validationMap[shortcode];
  if (!emoji) {
    return false;
  }

  // If we have a skin variation, check if this emoji supports it.
  const {variations} = emoji;
  const variation = variations && variations[Number(variationId)];
  if (variationId && !variation) {
    return false;
  }

  return true;
}

/** Check whether a unicode sequence is a valid Emoji. */
export function isValidEmoji(source: string, validationMap: EmojiMap) {
  // Check if this just one character.
  const parts = toArray(source);
  if (parts.length !== 1) {
    return false;
  }

  // Validate that it's a valid Emoji.
  const shortcode = unifiedToShortcode(source);
  return isValidShortcode(shortcode, validationMap);
}

/*
 * Representation transforms.
 */

let unifiedToShortcodeEmojiConvertor: EmojiConvertor | undefined;

/** Convert unicode emoji to colons representation. */
export function unifiedToShortcode(source: string) {
  if (!unifiedToShortcodeEmojiConvertor) {
    const convertor = buildEmojiConvertor();
    convertor.colons_mode = true;
    unifiedToShortcodeEmojiConvertor = convertor;
  }

  // Don't crash if we're not able to replace the source.
  try {
    return unifiedToShortcodeEmojiConvertor.replace_unified(source);
  } catch (err) {
    return source;
  }
}

let shortcodeToUnifiedEmojiConvertor: EmojiConvertor | undefined;

/** Convert colons emoji to unicode representation. */
export function shortcodeToUnified(source: string) {
  if (!shortcodeToUnifiedEmojiConvertor) {
    const convertor = buildEmojiConvertor();
    convertor.colons_mode = false;
    convertor.replace_mode = 'unified';
    shortcodeToUnifiedEmojiConvertor = convertor;
  }

  return shortcodeToUnifiedEmojiConvertor.replace_colons(source);
}

let shortcodeToUrlEmojiConvertor: EmojiConvertor | undefined;

/** Find the URL for any emoji representation. */
export function shortcodeToUrl(source: string, emojiMap: EmojiMap) {
  // Convert to colons.
  const colons = unifiedToShortcode(source);

  // Custom emojis take priority
  const emoji = emojiMap[source.slice(1, -1)];
  if (emoji && isCustomEmoji(emoji)) {
    return emoji.customUrl;
  }

  // Convert to img representation.
  if (!shortcodeToUrlEmojiConvertor) {
    const convertor = buildEmojiConvertor();
    convertor.colons_mode = false;
    convertor.replace_mode = 'img';
    convertor.supports_css = false;
    shortcodeToUrlEmojiConvertor = convertor;
  }

  const img = shortcodeToUrlEmojiConvertor.replace_colons(colons);

  // Extract the URL and return.
  const match = img.match(emojiUrlRegexp);
  return (match && match[1]) || '';
}

export interface EmojiDefinitionAndSkinTone {
  emoji: EmojiDefinition;
  skinTone?: SkinTonesEnum;
}

/** Converts an emoji character to an EmojiDefinition */
export const emojiToDefinition = memoize(Number.POSITIVE_INFINITY)(emojiToDefinitionBase);
function emojiToDefinitionBase(
  source: string,
  validationMap: EmojiMap,
): EmojiDefinitionAndSkinTone | undefined {
  const shortcode = unifiedToShortcode(source);
  const [shortcodeMatch, skinToneMatch] = compact(shortcode.split(':'));
  if (!shortcodeMatch) {
    return undefined;
  }

  const emoji = validationMap[shortcodeMatch];
  if (!emoji) {
    return undefined;
  }

  const matches = skinToneMatch && skinToneMatch.match(/[2-6]/);
  if (!matches) {
    return {emoji};
  }

  const skinTone = Number(matches[0]);
  return {emoji, skinTone};
}

export function splitUnifiedEmojis(text: string) {
  return text.split(unifiedEmojiSplitRegexp);
}

export function splitMarkdownEmojis(text: string) {
  return text.split(markdownEmojiSplitRegexp);
}

export interface EmojiTextPart {
  text: string;
  emojiAndSkinTone?: EmojiDefinitionAndSkinTone;
}
